"use client";

import "mapbox-gl/dist/mapbox-gl.css";
import "@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css";

import mapboxgl, { Map as MapboxMap, NavigationControl } from "mapbox-gl";
import type { MapClient, MapProps, MapSettings } from "../../../types";
import { useEffect, useRef, useState } from "react";
import { MarkerComponent } from "./Marker";
import { MapboxClient } from "../client";

const defaultMapSettings: MapSettings = {
  map: {
    zoom: 12,
    zoomable: true,
    pannable: true,
    center: {
      lng: -84.9836,
      lat: 29.7516,
    },
  },
};

export function MapComponent<T>({
  mapRef,
  token,
  data,
  settings,
  coordsGetter,
  keyGetter,
  renderMarker,
  renderPopup,
  onMapLoaded,
  onMapBoundsChanged,
  onCoordsClick,
  ...rest
}: MapProps<T, MapboxMap>) {
  const _settings = {
    ...defaultMapSettings,
    ...settings,
  };

  const internalMapRef = useRef<MapClient<MapboxMap> | null>(null);
  const _mapRef = mapRef ?? internalMapRef;

  const mapContapiner = useRef<HTMLDivElement | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [webglSupported, setWebglSupported] = useState(true);

  function emitBoundsChanged(map: MapboxMap) {
    const bounds = map.getBounds();
    if (!bounds) {
      return;
    }

    onMapBoundsChanged?.({
      sw: {
        lat: bounds._sw.lat,
        lng: bounds._sw.lng,
      },
      ne: {
        lat: bounds._ne.lat,
        lng: bounds._ne.lng,
      },
    });
  }

  useEffect(() => {
    if (!mapContapiner.current) {
      return;
    }

    if (!mapboxgl.supported()) {
      setWebglSupported(false);
      return;
    }

    const initBounds = settings?.map?.bounds;
    const map = new MapboxMap({
      container: mapContapiner.current,
      // Streets avoids Mapbox Standard's terrain DEM source, which can throw
      // "mapbox-dem already exists" when the map is recreated during navigation.
      style: "mapbox://styles/mapbox/streets-v12",
      ...(initBounds
        ? {
            bounds: [
              [initBounds.sw.lng, initBounds.sw.lat],
              [initBounds.ne.lng, initBounds.ne.lat],
            ],
          }
        : {
            center: settings?.map?.center ?? [-84.9836, 29.7516],
            zoom: settings?.map?.zoom ?? 12,
          }),
      minZoom: settings?.map?.minZoom,
      maxZoom: settings?.map?.maxZoom,
      accessToken: token,
      ...(_settings.map?.clickTolerance != null
        ? { clickTolerance: _settings.map.clickTolerance }
        : {}),
      projection: { name: "mercator" },
      cooperativeGestures: settings?.map?.scrollSafe,
    });

    if (_settings.map?.zoomable === false) {
      map.scrollZoom.disable();
      map.doubleClickZoom.disable();
      map.boxZoom.disable();
      map.touchZoomRotate.disable();
    } else {
      map.scrollZoom.enable();
      map.doubleClickZoom.enable();
      map.boxZoom.enable();
      map.touchZoomRotate.enable();
    }

    if (_settings.map?.pannable === false) {
      map.dragPan.disable();
      map.dragRotate.disable();
      map.keyboard.disable();
    } else {
      map.dragPan.enable();
      map.dragRotate.enable();
      map.keyboard.enable();
    }

    const mapClient = new MapboxClient(map);

    map.addControl(
      new NavigationControl({
        showZoom: _settings.map?.zoomable !== false,
        showCompass: _settings.map?.zoomable !== false,
      }),
    );

    map.on("load", () => {
      onMapLoaded?.(mapClient);
      setMapLoaded(true);
      emitBoundsChanged(map);
    });

    map.on("moveend", (e) => {
      if ("popupUpdate" in e && e.popupUpdate) {
        // HACK: moveend event triggered by a popup opening, do not emit
        // anything or else the map will rerender
        return;
      }

      emitBoundsChanged(e.target);
    });

    map.on("zoomend", (e) => {
      emitBoundsChanged(e.target);
    });

    map.on("click", (e) => {
      onCoordsClick?.({
        lat: e.lngLat.lat,
        lng: e.lngLat.lng,
      });
    });

    map.on("error", (e) => {
      console.warn("[MapboxGL]", e.error?.message ?? e);
    });

    const canvas = map.getCanvas();
    canvas.addEventListener("webglcontextlost", (e) => {
      e.preventDefault();
    });
    canvas.addEventListener("webglcontextrestored", () => {
      try {
        map.triggerRepaint();
      } catch {
        // Mapbox may throw during terrain re-init — safe to ignore
      }
    });

    _mapRef.current = mapClient;

    return () => {
      try {
        map.remove();
      } catch {
        // Mapbox aborts in-flight requests on remove, which throws
        // AbortError if the map hasn't fully loaded yet (common on mobile)
      }
      _mapRef.current = null;
    };
  }, []);

  return (
    <>
      <div ref={mapContapiner} {...rest}>
        {!webglSupported && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "12px",
              width: "100%",
              height: "100%",
              background: "#f5f5f4",
              borderRadius: "inherit",
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#a8a29e"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
            <div style={{ textAlign: "center" }}>
              <p
                style={{
                  margin: 0,
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#57534e",
                }}
              >
                Map unavailable
              </p>
              <p
                style={{
                  margin: "4px 0 0",
                  fontSize: "13px",
                  color: "#a8a29e",
                }}
              >
                Your browser doesn't support WebGL
              </p>
            </div>
          </div>
        )}
      </div>
      {mapLoaded &&
        _mapRef.current &&
        data &&
        data.map((item, index) => (
          <MarkerComponent
            key={keyGetter?.(item) ?? index}
            item={item}
            index={index}
            map={_mapRef.current}
            parentProps={{
              data,
              settings,
              coordsGetter,
              keyGetter,
              renderMarker,
              renderPopup,
              token,
            }}
          />
        ))}
    </>
  );
}

MapComponent.displayName = "MapComponent";
